import { Editor, Node } from '@tiptap/core';
import {
  EditorState,
  NodeSelection,
  Plugin,
} from '@tiptap/pm/state';

// Radius in px
export const buttonShapes = {
  square: { label: `Square`, radius: 0 },
  rounded: { label: `Rounded`, radius: 6 },
  pill: { label: `Pill`, radius: 9999 },
};

// In px
export const buttonSizes = {
  small: { label: `Small`, padY: 8, padX: 16, fontSize: 14 },
  medium: { label: `Medium`, padY: 12, padX: 24, fontSize: 16 },
  large: { label: `Large`, padY: 16, padX: 32, fontSize: 18 },
  full: { label: `Full width`, padY: 12, padX: 24, fontSize: 16 },
};

// Outlook lays the body out at 600px with 20px padding each side
const outlookContentWidth = 560;

export interface IEmailButtonAttrs {
  label: string;
  // Always a link merge tag, e.g. {{{InvoicePortalLink}}}
  href: string;
  backgroundColor: string;
  textColor: string;
  shape: keyof typeof buttonShapes;
  size: keyof typeof buttonSizes;
}

const hex = /^#[0-9a-f]{6}$/i;
const mergeTagHref = /^\{\{\{\w+\}\}\}$/;

const defaults: Omit<IEmailButtonAttrs, `href`> = {
  label: `Pay now`,
  backgroundColor: `#388e3c`,
  textColor: `#ffffff`,
  shape: `rounded`,
  size: `medium`,
};

// Values come back from pasted or AI written html too, so anything unexpected falls back
// to the default rather than ending up in the style attribute
const pick = <T extends string>(value: string | null, valid: (v: string) => boolean, fallback: T) => (value && valid(value) ? value : fallback) as T;

// Inline styles only, email clients drop style tags and classes
export function buttonStyle(attrs: IEmailButtonAttrs) {
  const size = buttonSizes[attrs.size] ?? buttonSizes.medium;

  return [
    attrs.size === `full` ? `display: block; text-align: center` : `display: inline-block`,
    `padding: ${size.padY}px ${size.padX}px`,
    `font-size: ${size.fontSize}px`,
    `line-height: 1.2`,
    `text-decoration: none`,
    `background-color: ${attrs.backgroundColor}`,
    `color: ${attrs.textColor}`,
    `border-radius: ${(buttonShapes[attrs.shape] ?? buttonShapes.rounded).radius}px`,
  ].join(`; `);
}

const escape = (text: string) => text.replace(/&/g, `&amp;`).replace(/</g, `&lt;`).replace(/>/g, `&gt;`).replace(/"/g, `&quot;`);

// Outlook on Windows ignores padding and radius on links, so it gets a VML shape instead,
// the usual "bulletproof button". VML needs a fixed size, so the width is guessed from the label.
// ponytail: width estimate per character, long or wide labels may clip; measure text if that comes up
export function outlookButton(attrs: IEmailButtonAttrs) {
  const size = buttonSizes[attrs.size] ?? buttonSizes.medium;
  const height = Math.round(size.fontSize * 1.2) + size.padY * 2;
  const width = attrs.size === `full` ? outlookContentWidth : Math.round(attrs.label.length * size.fontSize * 0.62) + size.padX * 2;
  // VML rounds corners by a percentage of the height, 50% being a pill
  const arcsize = Math.min(50, Math.round((buttonShapes[attrs.shape] ?? buttonShapes.rounded).radius / height * 100));

  return `<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${escape(attrs.href ?? ``)}" style="height:${height}px;v-text-anchor:middle;width:${width}px;" arcsize="${arcsize}%" stroke="f" fillcolor="${escape(attrs.backgroundColor)}"><w:anchorlock/><center style="color:${escape(attrs.textColor)};font-family:Arial,sans-serif;font-size:${size.fontSize}px;font-weight:bold;">${escape(attrs.label)}</center></v:roundrect>`;
}

export const selectedButton = (state: EditorState) => state.selection instanceof NodeSelection && state.selection.node.type.name === `emailButton`
  ? state.selection.node.attrs as IEmailButtonAttrs
  : null;

// Attribute steps keep the node selected, so the bubble menu stays open while editing
export const setButtonAttrs = (editor: Editor, attrs: Partial<IEmailButtonAttrs>) => editor.chain().command(({ tr }) => {
  Object.entries(attrs).forEach(([key, value]) => tr.setNodeAttribute(tr.selection.from, key, value));

  return true;
}).run();

export const insertButton = (editor: Editor, href: string) => editor.chain().focus()
  .insertContent({ type: `emailButton`, attrs: { href } })
  .command(({ tr }) => {
    tr.setSelection(NodeSelection.create(tr.doc, tr.selection.from - 1));

    return true;
  })
  .run();

// Inline so it sits in a paragraph and follows its alignment
export const EmailButtonNode = Node.create({
  name: `emailButton`,
  group: `inline`,
  inline: true,
  atom: true,
  // Styled by its own settings, not text marks. The label is bold through a strong tag rather than
  // font-weight, which the parser would read back as a bold mark
  marks: ``,
  selectable: true,
  draggable: true,
  addAttributes: () => ({
    label: { default: defaults.label, parseHTML: el => el.textContent.trim() || defaults.label },
    href: { default: null, parseHTML: el => el.getAttribute(`href`) },
    backgroundColor: { default: defaults.backgroundColor, parseHTML: el => pick(el.getAttribute(`data-bg`), v => hex.test(v), defaults.backgroundColor) },
    textColor: { default: defaults.textColor, parseHTML: el => pick(el.getAttribute(`data-text`), v => hex.test(v), defaults.textColor) },
    shape: { default: defaults.shape, parseHTML: el => pick(el.getAttribute(`data-shape`), v => v in buttonShapes, defaults.shape) },
    size: { default: defaults.size, parseHTML: el => pick(el.getAttribute(`data-size`), v => v in buttonSizes, defaults.size) },
  }),
  // Ahead of the link mark, which would otherwise take it as a plain link. Any other href stays a plain link
  parseHTML: () => [{ tag: `a[data-email-button]`, priority: 60, getAttrs: el => (mergeTagHref.test(el.getAttribute(`href`) ?? ``) ? null : false) }],
  // Outlook gets the VML shape, every other client the link. Built with DOM calls because
  // a render spec can't hold comments
  renderHTML: ({ node }) => {
    const attrs = node.attrs as IEmailButtonAttrs;
    const link = document.createElement(`a`);
    const label = document.createElement(`strong`);

    Object.entries({
      'data-email-button': ``,
      'data-bg': attrs.backgroundColor,
      'data-text': attrs.textColor,
      'data-shape': attrs.shape,
      'data-size': attrs.size,
      href: attrs.href ?? ``,
      target: `_blank`,
      rel: `noopener noreferrer`,
      style: buttonStyle(attrs),
    }).forEach(([key, value]) => link.setAttribute(key, value));

    label.textContent = attrs.label;
    link.append(label);

    const dom = document.createElement(`span`);

    dom.append(
      document.createComment(`[if mso]>${outlookButton(attrs)}<![endif]`),
      document.createComment(`[if !mso]><!`),
      link,
      document.createComment(`<![endif]`),
    );

    return { dom };
  },
  renderText: ({ node }) => node.attrs.label,
  // The link is not editable text, so the browser would follow it on click
  addProseMirrorPlugins: () => [new Plugin({
    props: {
      handleDOMEvents: Object.fromEntries([`click`, `auxclick`].map(type => [type, (_view, event: MouseEvent) => {
        if ((event.target as HTMLElement).closest?.(`a[data-email-button]`)) event.preventDefault();

        return false;
      }])),
    },
  })],
});
